import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, GoogleLogo, CaretDown, Export, FileText, DownloadSimple } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import { CollapsibleGroup, SectionBlock } from "./TalkingDocSections";

export function TalkingDocOutput({ result, violations, title, minutes,
  editingSection, setEditingSection, editText, setEditText, handleEditSave,
  handleRegenerateSection, regeneratingSection, handleRewriteChip, rewritingSection,
  handleCopyMarkdown, handleCopyOutline, handleCopyStructuredOutline, handleDownloadOutlineMd, handleDownloadOutlineTxt,
  handleExportGoogleDocs, handleExportOutlineGoogleDocs,
  handleExportTemplate, exportTemplates, exporting, brandPalette, onReset }) {

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const sectionNames = result?.section_names || [];
  const sections = result?.sections || {};
  const outline = result?.document_outline || result?.outline || result?.outline_notes || "";
  const meta = result?.metadata || {};

  // Brand-aware accent color for document outline
  const accentColor = brandPalette?.primary_color || "#FF4400";
  const accentColorLight = accentColor + "0D";

  // Group sections for visual layout
  const hookSections = sectionNames.filter(n => n.startsWith("Hook Option"));
  const trackSections = sectionNames.filter(n => n.startsWith("Talk Track"));
  const ctaSections = sectionNames.filter(n => n.includes("CTA"));
  const cutSections = sectionNames.filter(n => n.startsWith("Cut Line"));
  const otherSections = sectionNames.filter(n => !hookSections.includes(n) && !trackSections.includes(n) && !ctaSections.includes(n) && !cutSections.includes(n));

  const renderGroup = (groupLabel, sectionList, accent) => {
    if (sectionList.length === 0) return null;
    return (
      <CollapsibleGroup label={groupLabel} accent={accent} count={sectionList.length}>
        {sectionList.map(label => (
          <SectionBlock key={label} label={label} content={sections[label] || ""}
            isEditing={editingSection === label} editText={editText}
            onStartEdit={() => { setEditingSection(label); setEditText(sections[label] || ""); }}
            onCancelEdit={() => setEditingSection(null)}
            onSaveEdit={() => handleEditSave(label)}
            onEditTextChange={setEditText}
            onRegenerate={() => handleRegenerateSection(label)}
            isRegenerating={regeneratingSection === label}
            onRewriteChip={(chip) => handleRewriteChip(label, chip)}
            rewritingChip={rewritingSection?.startsWith(label) ? rewritingSection.split("-").slice(1).join("-") : null}
            hasViolation={violations[label]?.length > 0}
            violationPhrases={violations[label] || []}
          />
        ))}
      </CollapsibleGroup>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]" data-testid="td-result-title">{title}</h2>
          <p className="text-xs text-[var(--text-muted)]">
            {minutes} min &middot; {meta.goal} &middot; {meta.audience} &middot; {meta.tone_mode}
            {meta.tone_variations?.length > 0 && ` + ${meta.tone_variations.join(", ")}`}
            &middot; {meta.word_count || 0} words
            {meta.chunked && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-full">Section-by-section</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onReset} className="text-xs h-10 sm:h-8 bg-[#FF4400]/10 text-[#FF4400] hover:bg-[#FF4400]/20 border border-[#FF4400]/20" data-testid="td-new-btn">New Doc</Button>
        </div>
      </div>

      {/* Tabs: Script vs Outline */}
      <Tabs defaultValue="script" className="w-full">
        <TabsList className="bg-[var(--bg-card)] border border-[var(--border)] w-full sm:w-auto" data-testid="td-output-tabs">
          <TabsTrigger value="script" className="data-[state=active]:bg-[#FF4400]/10 data-[state=active]:text-[#FF4400] text-sm gap-1.5" data-testid="td-tab-script">
            <FileText size={14} /> Script
          </TabsTrigger>
          <TabsTrigger value="outline" className="data-[state=active]:bg-[#FF4400]/10 data-[state=active]:text-[#FF4400] text-sm gap-1.5" data-testid="td-tab-outline">
            <FileText size={14} /> Document Outline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="script" className="mt-4 space-y-4">
          {/* Script action bar */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleCopyMarkdown} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-copy-btn"><Copy size={14} />Copy Script</Button>
            <Button onClick={handleExportGoogleDocs} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-gdocs-btn"><GoogleLogo size={14} />Google Docs</Button>
            {/* Export templates dropdown */}
            <div className="relative">
              <Button onClick={() => setShowExportMenu(!showExportMenu)} variant="outline"
                disabled={exporting} className="text-xs h-10 sm:h-8 gap-1" data-testid="td-export-menu-btn">
                <Export size={14} />{exporting ? "Exporting..." : "Export As..."}
                <CaretDown size={12} />
              </Button>
              {showExportMenu && (
                <div className="absolute top-full mt-1 right-0 z-50 w-64 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-lg overflow-hidden" data-testid="td-export-dropdown">
                  {exportTemplates.map(tmpl => (
                    <button key={tmpl.key} onClick={() => { handleExportTemplate(tmpl.key); setShowExportMenu(false); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border)] last:border-0"
                      data-testid={`td-export-${tmpl.key}`}>
                      <span className="text-xs font-medium text-[var(--text-primary)]">{tmpl.name}</span>
                      <span className="block text-[10px] text-[var(--text-muted)]">{tmpl.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Script sections */}
          {renderGroup("Hooks", hookSections, "#FF4400")}
          {renderGroup("Talk Track", trackSections, "#3B82F6")}
          {renderGroup("Calls to Action", ctaSections, "#10B981")}
          {renderGroup("Cut Lines (Safe to Remove)", cutSections, "#F59E0B")}
          {renderGroup("Supporting Elements", otherSections, "#8B5CF6")}
        </TabsContent>

        <TabsContent value="outline" className="mt-4">
          {/* Outline action bar */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handleCopyOutline} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-copy-outline-btn"><Copy size={14} />Copy Document Outline</Button>
            <Button onClick={handleCopyStructuredOutline} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-copy-outline-md-btn"><Copy size={14} />Copy as structured markdown</Button>
            <Button onClick={handleDownloadOutlineMd} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-download-outline-md-btn"><DownloadSimple size={14} />Download .md</Button>
            <Button onClick={handleDownloadOutlineTxt} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-download-outline-txt-btn"><DownloadSimple size={14} />Download .txt</Button>
            <Button onClick={handleExportOutlineGoogleDocs} variant="outline" className="text-xs h-10 sm:h-8 gap-1" data-testid="td-gdocs-outline-btn"><GoogleLogo size={14} />Export Outline to Google Docs</Button>
            <Button
              onClick={() => setShowShareModal(true)}
              disabled={!outline}
              className="text-xs h-10 sm:h-8 gap-1 bg-[#FF4400] hover:bg-[#FF6633] text-white"
              data-testid="td-share-outline-btn"
            >
              <ShareNetwork size={14} />Share Outline
            </Button>
          </div>
          {outline ? (
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 sm:p-6" data-testid="td-outline-content"
              style={{ "--brand-accent": accentColor, "--brand-accent-light": accentColorLight }}>
              <div className="prose prose-sm prose-invert max-w-none text-[var(--text-secondary)] leading-relaxed
                prose-headings:font-semibold
                prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2
                prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1.5
                prose-strong:font-semibold
                prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:text-[var(--text-secondary)]
                prose-p:my-2 prose-ul:my-2 prose-ol:my-2"
                style={{
                  "--tw-prose-headings": accentColor,
                  "--tw-prose-bold": "var(--text-primary)",
                  "--tw-prose-links": accentColor,
                  "--tw-prose-bullets": accentColor,
                  "--tw-prose-counters": accentColor,
                  "--tw-prose-quote-borders": accentColor,
                }}>
                <ReactMarkdown>{outline}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 text-center" data-testid="td-outline-empty">
              <p className="text-sm text-[var(--text-muted)]">No document outline was generated. Try regenerating.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ShareOutlineModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={title}
        outline={outline}
      />
    </div>
  );
}
